// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
import type { Band, DSNSnapshot } from '../types';
import { extractPairs } from '../state';
import { getSpacecraftInfo, CATEGORY_COLOR } from '../data/spacecraft';
import { BAND_COLOR } from '../utils/bands';
import { formatDataRate } from '../utils/format';

export function renderFlow(snapshot: DSNSnapshot | null): string {
  if (!snapshot) {
    return `<div class="flow-wrap"><p style="color: var(--text-tertiary); text-align: center;">Loading…</p></div>`;
  }

  const pairs = extractPairs(snapshot).filter(
    (p) => p.spacecraft && p.spacecraft !== 'DSN' && p.spacecraft !== 'DSS',
  );

  const dishes: string[] = [];
  for (const complex of snapshot.complexes) {
    for (const dish of complex.dishes) {
      if (dish.activity.toLowerCase().includes('spacecraft')) dishes.push(dish.name);
    }
  }

  const spacecraft = [...new Set(pairs.map((p) => p.spacecraft))].sort();

  if (dishes.length === 0 || spacecraft.length === 0) {
    return `
      <div class="view-header">
        <h2 class="view-title">Signal flow</h2>
        <p class="view-subtitle">Live map of which antennas are talking to which spacecraft.</p>
      </div>
      <div class="flow-wrap">
        <p style="color: var(--text-tertiary); text-align: center; padding: var(--space-2xl);">
          No active tracks at this moment. All antennas appear to be idle, in maintenance, or in engineering mode.
        </p>
      </div>
    `;
  }

  const leftX = 120;
  const rightX = 680;
  const topPadding = 40;
  const rowHeight = 44;
  const totalRows = Math.max(dishes.length, spacecraft.length);
  const height = topPadding + totalRows * rowHeight + 40;

  const dishY: Record<string, number> = {};
  dishes.forEach((d, i) => {
    dishY[d] = topPadding + (height - 80) * (i + 0.5) / dishes.length;
  });

  const scY: Record<string, number> = {};
  spacecraft.forEach((s, i) => {
    scY[s] = topPadding + (height - 80) * (i + 0.5) / spacecraft.length;
  });

  const ribbons = pairs
    .map((p) => {
      const y1 = dishY[p.dish];
      const y2 = scY[p.spacecraft];
      if (y1 === undefined || y2 === undefined) return '';
      const cx1 = leftX + 180;
      const cx2 = rightX - 180;
      const band = (p.bands[0] ?? '') as Band;
      const color = BAND_COLOR[band] ?? '#7f8c8d';
      const active = p.activeDown || p.activeUp;
      const strokeWidth = active ? 3 : 1.2;
      const opacity = active ? 0.82 : 0.28;
      const maxRate = Math.max(0, ...p.dataRates);
      const tip = `${p.dish} → ${getSpacecraftInfo(p.spacecraft).name} · ${band ? `${band}-band` : 'no band'} · ${active ? 'signal active' : 'scheduled'}${maxRate > 0 ? ` · ${formatDataRate(maxRate)}` : ''}`;
      return `
        <path d="M ${leftX + 10} ${y1} C ${cx1} ${y1}, ${cx2} ${y2}, ${rightX - 10} ${y2}"
              stroke="${color}" stroke-width="${strokeWidth}" fill="none" opacity="${opacity}"
              stroke-linecap="round" data-tip="${tip}" aria-label="${tip}"/>
      `;
    })
    .join('');

  const dishNodes = dishes
    .map((d) => {
      const y = dishY[d];
      const complex = snapshot.complexes.find((c) => c.dishes.some((dd) => dd.name === d));
      const targets = [...new Set(pairs.filter((p) => p.dish === d).map((p) => getSpacecraftInfo(p.spacecraft).name))];
      const tip = `${d} · ${complex?.friendlyName ?? 'Unknown complex'} — tracking ${targets.length} spacecraft${targets.length > 0 ? `: ${targets.join(', ')}` : ''}`;
      return `
        <g transform="translate(${leftX}, ${y})" data-tip="${tip}" aria-label="${tip}">
          <rect x="-80" y="-14" width="92" height="28" rx="14" fill="var(--bg-elevated)" stroke="var(--border-default)"/>
          <text class="flow-node" x="-70" y="3">${d}</text>
          <text class="flow-sub" x="-70" y="16">${complex?.friendlyName ?? ''}</text>
        </g>
      `;
    })
    .join('');

  const scNodes = spacecraft
    .map((s) => {
      const y = scY[s];
      const info = getSpacecraftInfo(s);
      const color = CATEGORY_COLOR[info.category];
      const antennas = [...new Set(pairs.filter((p) => p.spacecraft === s).map((p) => p.dish))];
      const tip = `${info.name} — ${info.mission} · on ${antennas.length} antenna${antennas.length === 1 ? '' : 's'}: ${antennas.join(', ')}`;
      return `
        <g transform="translate(${rightX}, ${y})" data-tip="${tip}" aria-label="${tip}">
          <rect x="-8" y="-14" width="180" height="28" rx="14" fill="var(--bg-elevated)" stroke="${color}"/>
          <circle cx="6" cy="0" r="4" fill="${color}"/>
          <text class="flow-node" x="18" y="3">${info.name}</text>
          <text class="flow-sub" x="18" y="16">${info.mission}</text>
        </g>
      `;
    })
    .join('');

  const bandLegend = Object.entries(BAND_COLOR)
    .filter(([k]) => k !== '')
    .map(([k, c]) => `<span style="display:inline-flex;align-items:center;gap:4px;color:var(--text-secondary);"><span style="width:14px;height:2px;background:${c};"></span>${k}-band</span>`)
    .join(' · ');

  return `
    <div class="view-header">
      <h2 class="view-title">Signal flow</h2>
      <p class="view-subtitle">
        Which antennas are talking to which spacecraft right now. Thicker lines mean at least one active signal —
        thin ghosted lines show scheduled tracks where the signal is momentarily inactive.
        Line colour is the <span class="glossary-link" data-term="xband">radio band</span>.
      </p>
    </div>
    <div class="flow-wrap">
      <svg id="flow-svg" viewBox="0 0 800 ${height}" preserveAspectRatio="xMidYMid meet">
        <text class="flow-section-label" x="${leftX - 80}" y="20">Antennas (${dishes.length})</text>
        <text class="flow-section-label" x="${rightX - 8}" y="20">Spacecraft (${spacecraft.length})</text>
        ${ribbons}
        ${dishNodes}
        ${scNodes}
      </svg>
      <div style="display:flex; gap: var(--space-md); flex-wrap: wrap; margin-top: var(--space-md); font-size: var(--font-size-xs);">
        ${bandLegend}
      </div>
    </div>
  `;
}
